import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Tabs, Tab, Spinner } from "react-bootstrap";
import { formatPrice } from "utils/utils";
import { getRequest } from "services/api";

const BookingDetailModal = ({ show, onHide, booking }) => {
  const [activeTab, setActiveTab] = useState("income");
  const [reservationData, setReservationData] = useState(null);
  const [loadingReservation, setLoadingReservation] = useState(false);

  useEffect(() => {
    if (show && booking && activeTab === "reservation" && !reservationData) {
      const fetchReservation = async () => {
        setLoadingReservation(true);
        try {
          const includes = "activities, trip, agent, host, paymentMethod, productAgent, customer, labels, photographers, guides, count_logs, logs, reservationMeal, meals, revisions, revision_approved, revision_rejected, akuntansi, revisions.user, revision_approved.user, revision_rejected.user";
          const response = await getRequest(`/reservation/${booking.id}?include=${includes.replace(/ /g, "")}`);
          if (response.success) {
            setReservationData(response.data);
          }
        } catch (error) {
          console.error("Failed to fetch reservation details", error);
        } finally {
          setLoadingReservation(false);
        }
      };
      fetchReservation();
    }
  }, [show, booking, activeTab, reservationData]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!show) {
      setActiveTab("income");
      setReservationData(null);
    }
  }, [show]);

  if (!booking) return null;

  const shouldShowField = (key) => {
    const k = key.toLowerCase();
    if (k === 'id' || k.endsWith('_id')) return false;
    if (k === 'created_at' || k === 'updated_at' || k === 'deleted_at' || k === 'email_verified_at') return false;
    if (k === 'logs' || k === 'count_logs' || k === 'revisions' || k === 'activities') return false;
    if (k.startsWith('fingerspot_') || k === 'photo' || k === 'photo_url' || k === 'password') return false;
    return true;
  };

  const renderValue = (key, value) => {
    if (value === null || value === undefined) return "-";

    // Handle specific relations to only show name
    if (key === "host" && typeof value === "object" && value !== null) {
      return value.name || "-";
    }

    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (value.length === 0) return "Empty";
        return (
          <div className="ps-2 border-start border-2 border-primary mb-1 mt-1">
            {value.map((v, i) => (
              <div key={i} className="mb-2">
                <span className="badge bg-secondary mb-1">Item #{i + 1}</span>
                {typeof v === "object" && v !== null ? (
                  <div className="small">
                    {Object.entries(v)
                      .filter(([k]) => shouldShowField(k))
                      .map(([k, val]) => (
                      <div key={k}>
                        <strong>{k.replace(/_/g, " ")}:</strong> {renderValue(k, val)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>{v}</div>
                )}
              </div>
            ))}
          </div>
        );
      }
      return (
        <div className="small ps-2 border-start border-2 border-success mb-1 mt-1">
          {Object.entries(value)
            .filter(([k]) => shouldShowField(k))
            .map(([k, val]) => (
            <div key={k}>
              <strong>{k.replace(/_/g, " ")}:</strong> {renderValue(k, val)}
            </div>
          ))}
        </div>
      );
    }

    const priceKeywords = ["price", "bill", "susuk", "net", "others", "kuber", "deduction", "amount"];
    const isPrice = priceKeywords.some(kw => key.toLowerCase().includes(kw)) && !key.toLowerCase().includes("id");
    
    if (isPrice && !isNaN(value) && value !== "") {
      return formatPrice(Number(value));
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    return value.toString();
  };

  const renderTable = (data) => {
    if (!data) return null;
    return (
      <div className="table-responsive">
        <Table striped bordered hover size="sm">
          <thead>
            <tr>
              <th>Field</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data)
              .filter(([key]) => shouldShowField(key))
              .map(([key, value]) => (
                <tr key={key}>
                  <td className="text-capitalize">{key.replace(/_/g, " ")}</td>
                  <td>{renderValue(key, value)}</td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderActivities = (data) => {
    if (!data?.activities || data.activities.length === 0) return null;
    return (
      <div className="mt-4">
        <h5>Activities</h5>
        <Table striped bordered hover size="sm">
          <thead>
            <tr>
              <th>Activity</th>
              <th>Qty</th>
              <th>Price</th>
            </tr>
          </thead>
          <tbody>
            {data.activities.map((a, i) => (
              <tr key={i}>
                <td>{a.activity || a.name || a.activity_name || "-"}</td>
                <td>{a.qty}</td>
                <td>{formatPrice(a.price || a.total_price || 0)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Booking Details - {booking.code}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: '400px' }}>
        <Tabs
          id="booking-detail-tabs"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          <Tab eventKey="income" title="Income Data">
            {renderTable(booking)}
            {renderActivities(booking)}
          </Tab>
          <Tab eventKey="reservation" title="Reservation Data">
            {loadingReservation ? (
              <div className="text-center mt-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading reservation details...</p>
              </div>
            ) : reservationData ? (
              <>
                {renderTable(reservationData)}
                {renderActivities(reservationData)}
              </>
            ) : (
              <div className="text-center mt-5 text-muted">
                <p>Failed to load reservation data or data not found.</p>
              </div>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BookingDetailModal;
